/**
 * MAVLink 协议数据的类型定义。
 *
 * 这些类型是**手写**的，生成器只负责往里填数据 —— 这样"帧是怎么构成的"这件事
 * 由人读得懂的类型定义说了算，而不是由生成器模板说了算。
 */

/** MAVLink 的字段基础类型。`uint8_t_mavlink_version` 是协议里的"魔法类型"。 */
export type FieldTypeName =
  | 'char'
  | 'int8_t'
  | 'uint8_t'
  | 'uint8_t_mavlink_version'
  | 'int16_t'
  | 'uint16_t'
  | 'int32_t'
  | 'uint32_t'
  | 'int64_t'
  | 'uint64_t'
  | 'float'
  | 'double';

/** 每种基础类型在线上占几个字节。 */
export const FIELD_TYPE_SIZES: Readonly<Record<FieldTypeName, number>> = {
  char: 1,
  int8_t: 1,
  uint8_t: 1,
  uint8_t_mavlink_version: 1,
  int16_t: 2,
  uint16_t: 2,
  int32_t: 4,
  uint32_t: 4,
  int64_t: 8,
  uint64_t: 8,
  float: 4,
  double: 8,
};

export interface FieldDefinition {
  readonly name: string;
  /** XML 里的**原始**类型名。`uint8_t_mavlink_version` 保持原样，CRC_EXTRA 依赖它。 */
  readonly type: FieldTypeName;
  /** 0 表示标量，大于 0 表示定长数组的元素个数 */
  readonly arrayLength: number;
  /** MAVLink 2 扩展字段：v1 帧里不发送，v2 帧里缺失时按零填充 */
  readonly extension: boolean;
  /** 该字段应按哪个枚举解释 */
  readonly enumName?: string;
  /** 字段是位掩码而非取值枚举 */
  readonly isBitmask?: boolean;
  readonly units?: string;
  readonly printFormat?: string;
  readonly multiplier?: string;
  readonly defaultValue?: string;
  readonly minValue?: string;
  readonly maxValue?: string;
  /** 表示"无数据"的哨兵值（例如 `UINT16_MAX`） */
  readonly invalid?: string;
}

export interface MessageDefinition {
  readonly id: number;
  readonly name: string;
  /**
   * CRC_EXTRA。它与字段顺序、字段类型、数组长度绑定，
   * 算错的表现是**对端把每一帧都当损坏帧丢掉**，而不是报一个明确的错。
   */
  readonly crcExtra: number;
  /** 完整线上字节数，含扩展字段；不含 MAVLink 2 的尾部零截断 */
  readonly wireLength: number;
  /** **线上顺序**的字段（已按类型宽度降序排列），不是 XML 的书写顺序 */
  readonly fields: readonly FieldDefinition[];
}

export interface EnumEntryDefinition {
  readonly name: string;
  readonly value: number;
  /**
   * MAV_CMD 条目的分类位。任务规划器靠它们判断"这条命令能不能放进航线"、
   * "它带不带坐标"、"是不是围栏或备降点命令"。
   * 这些属性只在 XML 里出现，运行时无法从别处推断。
   */
  readonly command?: boolean;
  readonly mission?: boolean;
  readonly hasLocation?: boolean;
  readonly isDestination?: boolean;
  readonly fence?: boolean;
  readonly rally?: boolean;
}

export interface EnumDefinition {
  readonly name: string;
  readonly isBitmask: boolean;
  readonly entries: readonly EnumEntryDefinition[];
}

/** MAV_CMD 条目的参数说明（只给人看，不参与编解码） */
export interface CommandParamNote {
  readonly index: number;
  readonly label?: string;
  readonly units?: string;
  readonly enumName?: string;
  readonly minValue?: string;
  readonly maxValue?: string;
  readonly increment?: string;
  readonly defaultValue?: string;
  readonly description?: string;
}

export interface MessageNotes {
  readonly description?: string;
  readonly fields?: Readonly<Record<string, string>>;
}

export interface EnumEntryNotes {
  readonly description?: string;
  readonly params?: readonly CommandParamNote[];
}

export interface EnumNotes {
  readonly description?: string;
  readonly entries: Readonly<Record<string, EnumEntryNotes>>;
}

/**
 * 当前 MAVLink 版本号，来自上游的 `<version>`。
 * 发送方把 `uint8_t_mavlink_version` 类型的字段填成它（`HEARTBEAT.mavlink_version`）。
 */
export const MAVLINK_VERSION = 3;

/** `HEARTBEAT` 的 `custom_mode` 是 32 位自定义模式字段，ArduPilot 用低 8 位放飞行模式编号 */
export const CUSTOM_MODE_MASK = 0xff;
